import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Scale, Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function Login() {
  const [form, setForm] = useState({ email: 'admin@mutatupartners.co.zw', password: 'Admin@1234' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col justify-center px-16 flex-1 text-white">
        <div className="flex items-center gap-4 mb-10">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
            <Scale size={30} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Mutatu and Partners</h1>
            <p className="text-blue-200">Legal Practice Management System</p>
          </div>
        </div>
        <h2 className="text-4xl font-bold mb-4 leading-tight">
          Manage your law firm<br />with confidence.
        </h2>
        <p className="text-blue-200 text-lg mb-10">
          Clients, cases, billing, trust accounts, documents, and team collaboration — all in one place.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {['Client Management', 'Case Tracking', 'Trust Accounts (IOLTA)', 'Document Templates',
            'Time & Billing', 'Reports & Analytics'].map(f => (
            <div key={f} className="flex items-center gap-2 text-blue-200">
              <div className="w-5 h-5 bg-blue-400/30 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-300 rounded-full" />
              </div>
              <span className="text-sm">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex items-center justify-center w-full lg:w-[460px] flex-shrink-0 p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1e3a5f' }}>
              <Scale size={22} className="text-white" />
            </div>
            <div>
              <div className="font-bold text-gray-900">Mutatu and Partners</div>
              <div className="text-xs text-gray-500">Kwekwe, Zimbabwe</div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h2>
          <p className="text-gray-500 text-sm mb-7">Sign in to your account to continue</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
              <AlertCircle size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className="input pr-10"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPass(!showPass)}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-white font-semibold transition-all duration-200 mt-2"
              style={{ backgroundColor: loading ? '#93c5fd' : '#1e3a5f' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-600 font-medium mb-1">Mutatu and Partners — Demo</p>
            <p className="text-xs text-blue-500">admin@mutatupartners.co.zw / Admin@1234</p>
          </div>
        </div>
      </div>
    </div>
  );
}
